import wx from 'weixin-js-sdk';
import qs from 'qs';
import shareLogo from '@/assets/images/share-logo.jpg';
import { browerVersion } from '../utils';

export interface ShareParams {
  title?: string;
  desc?: string;
  link?: string;
  imgUrl?: string;
}

const defaultShareTitle = import.meta.env.VITE_WECHAT_SHARE_TITLE;
const defaultShareDesc = import.meta.env.VITE_WECHAT_SHARE_DESC;

const mergeShareParams = (params: ShareParams) => {
  const { title, desc, link, imgUrl } = params;
  return {
    title: title || defaultShareTitle,
    desc: desc || defaultShareDesc,
    link: link || window.location.href,
    imgUrl: imgUrl || shareLogo,
  };
};

const checkJsApiResultFormat = (value: string | boolean) => {
  if (typeof value === 'string' && value === 'true') {
    return true;
  }
  if (typeof value === 'boolean' && value === true) {
    return true;
  }
  return false;
};

const updateShareParams = (shareParams: ShareParams) => {
  const {
    title = defaultShareTitle,
    desc = defaultShareDesc,
    link = window.location.href,
    imgUrl = shareLogo,
  } = shareParams;
  const shareAppMessage = { title, desc, link, imgUrl, success: () => {} };
  const shareTimelineMessage = { title, link, imgUrl, success: () => {} };
  wx.checkJsApi({
    jsApiList: [
      'updateAppMessageShareData',
      'updateTimelineShareData',
      'onMenuShareAppMessage',
      'onMenuShareTimeline',
    ],
    success: (res: any) => {
      if (!res || !res.checkResult) {
        wx.updateAppMessageShareData(shareAppMessage);
        wx.updateTimelineShareData(shareTimelineMessage);
        return;
      }
      const {
        updateAppMessageShareData,
        onMenuShareAppMessage,
        updateTimelineShareData,
        onMenuShareTimeline,
      } = res.checkResult;
      if (checkJsApiResultFormat(updateAppMessageShareData)) {
        wx.updateAppMessageShareData(shareAppMessage);
      } else if (checkJsApiResultFormat(onMenuShareAppMessage)) {
        wx.onMenuShareAppMessage({ ...shareAppMessage, cancel: () => {} });
      }
      if (checkJsApiResultFormat(updateTimelineShareData)) {
        wx.updateTimelineShareData(shareTimelineMessage);
      } else if (checkJsApiResultFormat(onMenuShareTimeline)) {
        wx.onMenuShareTimeline({ ...shareTimelineMessage, cancel: () => {} });
      }
    },
    fail: (err: any) => {
      console.error(err);
      wx.updateAppMessageShareData(shareAppMessage);
      wx.updateTimelineShareData(shareTimelineMessage);
    },
  });
};

const useWxShareStore = defineStore('wxShare', {
  state: () => {
    return {
      isRequesting: false,
      isFinishConfig: false,
      shareParams: {
        title: defaultShareTitle,
        desc: defaultShareDesc,
        link: window.location.href,
        imgUrl: shareLogo,
      },
    };
  },
  actions: {
    async initWxShare(params?: ShareParams) {
      if (!browerVersion().weixin) {
        return;
      }
      if (params) {
        this.shareParams = mergeShareParams(params);
      }
      if (this.isFinishConfig) {
        updateShareParams(this.shareParams);
      }
      if (!this.isRequesting) {
        this.isRequesting = true;
        const url = window.location.href.split('#')[0];
        const res = await fetch(
          `${import.meta.env.VITE_MAIN_DOMAIN}/auxiliaryapi/api/content/wxShare/wechatParam?${qs.stringify(
            {
              url,
            },
          )}`,
        );
        const data = await res.json();
        wx.config({
          appId: data.appid,
          timestamp: data.timestamp,
          nonceStr: data.nonceStr,
          signature: data.signature,
          jsApiList: [
            'updateAppMessageShareData',
            'updateTimelineShareData',
            'onMenuShareAppMessage',
            'onMenuShareTimeline',
          ],
        });
        wx.ready(() => {
          this.isFinishConfig = true;
          updateShareParams(this.shareParams);
        });
      }
    },
  },
});

export default useWxShareStore;
